import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  BuildingOfficeIcon, 
  UserIcon, 
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ArrowLeftIcon,
  PencilIcon,
  EyeIcon,
  PlusIcon,
  ChartBarIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import AuditHistoryButton from '../components/audit/AuditHistoryButton';
import { AUDIT_MODULES } from '../services/system/audit';
import { clientsService } from '../services/logistics/clients';
import { shipmentsService } from '../services/logistics/shipments';
import { driversService } from '../services/logistics/drivers';
import { vehiclesService } from '../services/logistics/vehicles';
import { truckTypesService } from '../services/logistics/truck-types';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { saudiCities } from '../data/saudiCities';

const ClientDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [activeTab, setActiveTab] = useState('shipments');
  
  // Additional data for driver and vehicle info
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [truckTypes, setTruckTypes] = useState([]);
  
  // Filter states
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Selection and modal states
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [selectedShipmentDetails, setSelectedShipmentDetails] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Helper function to get city name in current language
  const getCityName = (cityKey) => {
    if (!cityKey) return cityKey;
    
    const city = saudiCities.find(c => c.value === cityKey);
    
    if (city) {
      return city.label[i18n.language] || city.label.en;
    }
    
    return cityKey;
  };

  // Helper function to format route display for new route system
  const formatRouteDisplay = (shipment) => {
    // If shipment has routeCities (new system), use that
    if (shipment.routeCities && shipment.routeCities.length > 0) {
      const validCities = shipment.routeCities.filter(city => city.city);
      
      if (validCities.length === 0) {
        return { origin: 'N/A', destination: 'N/A' };
      }
      
      if (validCities.length === 1) {
        const cityName = getCityName(validCities[0].city);
        return { origin: cityName, destination: cityName };
      }
      
      // Get origin (first city)
      const originCity = getCityName(validCities[0].city);
      
      // Get destination cities (excluding origin)
      const destinationCities = validCities.slice(1).map(city => getCityName(city.city));
      
      if (destinationCities.length === 0) {
        return { origin: originCity, destination: originCity };
      }
      
      if (destinationCities.length === 1) {
        return { origin: originCity, destination: destinationCities[0] };
      }
      
      // Multiple destination cities - format as "City1 - City2 - City3"
      // If there are 3 or more destination cities, exclude the first destination city
      let citiesToShow = destinationCities;
      if (destinationCities.length >= 3) {
        citiesToShow = destinationCities.slice(1); // Skip first destination city
      }
      
      const destinationDisplay = citiesToShow.join(' - ');
      
      return { origin: originCity, destination: destinationDisplay };
    }
    
    // Fallback to old system
    return {
      origin: getCityName(shipment.originCity) || shipment.origin || 'N/A',
      destination: getCityName(shipment.destinationCity) || shipment.destination || 'N/A'
    };
  };

  useEffect(() => {
    const fetchClientData = async () => {
      if (!currentCompany?.id || !id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch client, shipments, drivers, vehicles, and truck types
        const [clientData, shipmentsData, driversData, vehiclesData, truckTypesData] = await Promise.all([
          clientsService.getClient(id, currentCompany?.id),
          shipmentsService.getShipments(currentCompany.id),
          driversService.getDrivers(currentCompany.id),
          vehiclesService.getVehicles(currentCompany.id),
          truckTypesService.getTruckTypes(currentCompany.id)
        ]);

        if (clientData) {
          // Filter shipments for this client and sort by date (latest first)
          const clientShipments = shipmentsData
            .filter(s => s.clientId === id)
            .sort((a, b) => {
              const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
              const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
              return dateB - dateA; // Latest first
            });
          
          // Calculate statistics
          const totalRevenue = clientShipments.reduce((sum, s) => sum + (s.cost || 0), 0);
          const totalShipments = clientShipments.length;
          const avgShipmentValue = totalShipments > 0 ? totalRevenue / totalShipments : 0;
          const lastShipment = clientShipments.length > 0 
            ? new Date(Math.max(...clientShipments.map(s => new Date(s.createdAt?.toDate ? s.createdAt.toDate() : s.createdAt)))).toLocaleDateString('en-US')
            : null;

          setClient({
            ...clientData,
            totalShipments,
            totalRevenue,
            avgShipmentValue,
            lastShipment,
            paymentTerms: clientData.paymentTerms || 30,
            creditLimit: clientData.creditLimit || 50000,
            status: clientData.status || 'active'
          });
          
          setShipments(clientShipments);
          setFilteredShipments(clientShipments);
          setDrivers(driversData);
          setVehicles(vehiclesData);
          setTruckTypes(truckTypesData);
        }
      } catch (error) {
        console.error('Error fetching client data:', error);
        toast.error('Failed to load client details');
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [id, currentCompany?.id]);

  // Filter shipments based on selected filters
  useEffect(() => {
    let filtered = [...shipments];

    // Payment filter - based on a payment status field (we'll mock this for now)
    if (paymentFilter !== 'all') {
      const isPaid = paymentFilter === 'paid';
      filtered = filtered.filter(shipment => {
        // Mock payment status - in real app this would be from invoice/payment data
        const mockPaid = shipment.status === 'delivered' || shipment.paymentStatus === 'paid';
        return isPaid ? mockPaid : !mockPaid;
      });
    }

    // Date range filter
    if (dateFromFilter || dateToFilter) {
      filtered = filtered.filter(shipment => {
        const shipmentDate = shipment.createdAt?.toDate ? 
          shipment.createdAt.toDate() : 
          new Date(shipment.createdAt);
        
        if (dateFromFilter && dateToFilter) {
          const fromDate = new Date(dateFromFilter);
          const toDate = new Date(dateToFilter);
          toDate.setHours(23, 59, 59, 999);
          return shipmentDate >= fromDate && shipmentDate <= toDate;
        } else if (dateFromFilter) {
          const fromDate = new Date(dateFromFilter);
          return shipmentDate >= fromDate;
        } else if (dateToFilter) {
          const toDate = new Date(dateToFilter);
          toDate.setHours(23, 59, 59, 999);
          return shipmentDate <= toDate;
        }
        return true;
      });
    }

    // City filter
    if (cityFilter !== 'all') {
      filtered = filtered.filter(shipment => 
        shipment.originCity === cityFilter || shipment.destinationCity === cityFilter
      );
    }

    setFilteredShipments(filtered);
  }, [shipments, paymentFilter, dateFromFilter, dateToFilter, cityFilter]);

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      inactive: 'badge-gray',
      pending: 'badge-warning',
      sent: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      in_transit: 'badge-info',
      delivered: 'badge-success'
    };

    const statusLabels = {
      active: t('common.active', t('common.active')),
      inactive: t('common.inactive', t('common.inactive')),
      pending: t('shipments.pending', t('common.pending')),
      sent: t('accounting.filter.sent'),
      paid: t('shipments.paid', t('common.paid')),
      overdue: t('accounting.filter.overdue'),
      in_transit: t('shipments.in_transit', 'In Transit'),
      delivered: t('shipments.delivered', 'Delivered')
    };

    return <span className={`badge ${badges[status] || 'badge-gray'}`}>{statusLabels[status] || status}</span>;
  };

  const formatDateTime = (date) => {
    if (!date) return { date: t('common.not_specified', 'Not Specified'), time: '' };
    const d = date.toDate ? date.toDate() : new Date(date);
    
    // Always use English locale for Georgian calendar and English numerals
    const dateStr = d.toLocaleDateString('en-US');
    const timeStr = d.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    return { date: dateStr, time: timeStr };
  };

  // Calculate filtered totals with English numerals
  const filteredTotal = filteredShipments.reduce((sum, s) => sum + (s.cost || 0), 0);
  const hasActiveFilters = paymentFilter !== 'all' || dateFromFilter || dateToFilter || cityFilter !== 'all';

  // Get shipment number (use actual tracking number from data)
  const getShipmentNumber = (shipment) => {
    return shipment.trackingNumber || `SH${shipment.id?.substring(0, 8).toUpperCase() || 'UNKNOWN'}`;
  };

  // Handle shipment selection
  const handleShipmentSelect = (shipmentId) => {
    setSelectedShipments(prev => 
      prev.includes(shipmentId) 
        ? prev.filter(id => id !== shipmentId)
        : [...prev, shipmentId]
    );
  };

  // Handle select all shipments
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedShipments(filteredShipments.map(s => s.id));
    } else {
      setSelectedShipments([]);
    }
  };

  // Handle bulk payment status update
  const handleBulkPaymentUpdate = async (isPaid) => {
    if (selectedShipments.length === 0) {
      toast.error('يرجى تحديد شحنات أولاً');
      return;
    }

    setBulkActionLoading(true);
    try {
      // Update payment status for selected shipments
      const updates = selectedShipments.map(shipmentId => 
        shipmentsService.updateShipment(shipmentId, { 
          paymentStatus: isPaid ? 'paid' : 'unpaid',
          updatedAt: new Date()
        })
      );

      await Promise.all(updates);

      // Update local state
      setShipments(prev => prev.map(shipment => 
        selectedShipments.includes(shipment.id) 
          ? { ...shipment, paymentStatus: isPaid ? 'paid' : 'unpaid' }
          : shipment
      ));

      setSelectedShipments([]);
      toast.success(`تم تحديث حالة الدفع لـ ${selectedShipments.length} شحنة`);
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('فشل في تحديث حالة الدفع');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Handle showing shipment details
  const handleShowShipmentDetails = (shipment) => {
    setSelectedShipmentDetails(shipment);
    setShowShipmentModal(true);
  };

  // Check if all filtered shipments are selected
  const allSelected = filteredShipments.length > 0 && selectedShipments.length === filteredShipments.length;
  const someSelected = selectedShipments.length > 0 && selectedShipments.length < filteredShipments.length;

  // Helper functions for driver and vehicle info (same as Shipments page)
  const getDriverInfo = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver || null;
  };

  const getVehicleInfo = (vehicleIdOrPlate) => {
    // Try to find by ID first (both string and number)
    let vehicle = vehicles.find(v => v.id === vehicleIdOrPlate || v.id === parseInt(vehicleIdOrPlate));
    
    // If not found, try to find by plate number
    if (!vehicle) {
      vehicle = vehicles.find(v => v.plateNumber === vehicleIdOrPlate);
    }
    
    return vehicle || null;
  };

  // Helper function to get truck type name
  const getTruckTypeName = (truckTypeValue) => {
    if (!truckTypeValue) return t('common.not_specified', 'Not specified');
    
    const truckType = truckTypes.find(tt => tt.value === truckTypeValue);
    if (truckType && truckType.name) {
      return truckType.name[i18n.language] || truckType.name.en;
    }
    
    return truckTypeValue;
  };

  // Helper function to calculate total shipment price including all charges
  const calculateTotalShipmentPrice = (shipment) => {
    if (!shipment) return 0;
    
    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    // Calculate VAT
    let mainBasePrice, mainVatAmount;
    let backhaulVatAmount = backhaulPrice * 0.15;
    let itemizedVatAmount = itemizedTotal * 0.15;

    if (shipment.isVatIncluded) {
      // Main price includes VAT, backhaul and itemized costs are without VAT
      mainBasePrice = baseShipmentPrice / 1.15;
      mainVatAmount = baseShipmentPrice - mainBasePrice;
    } else {
      // All prices are without VAT
      mainBasePrice = baseShipmentPrice;
      mainVatAmount = baseShipmentPrice * 0.15;
    }

    const totalBasePrice = mainBasePrice + backhaulPrice + itemizedTotal;
    const totalVatAmount = mainVatAmount + backhaulVatAmount + itemizedVatAmount;
    const grandTotal = totalBasePrice + totalVatAmount;
    
    return grandTotal;
  };

  // Helper function to calculate subtotal before tax
  const calculateSubtotalBeforeTax = (shipment) => {
    if (!shipment) return 0;
    
    const baseShipmentPrice = parseFloat(shipment.cost || 0);
    const backhaulPrice = parseFloat(shipment.backhaulPrice || 0);
    const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    let mainBasePrice;

    if (shipment.isVatIncluded) {
      // Main price includes VAT, extract base price
      mainBasePrice = baseShipmentPrice / 1.15;
    } else {
      // All prices are without VAT
      mainBasePrice = baseShipmentPrice;
    }

    // Subtotal = base price + backhaul + itemized costs (all without VAT)
    const subtotal = mainBasePrice + backhaulPrice + itemizedTotal;
    
    return subtotal;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('clients.client_not_found', 'Client Not Found')}</h2>
          <button 
            onClick={() => navigate('/clients')}
            className="btn btn-primary"
          >
            {t('clients.back_to_clients', 'Back to Clients')}
          </button>
        </div>
      </div>
    );
  }

  // Get all unique cities from shipments for filter
  const allCities = [...new Set(shipments.flatMap(s => [s.originCity, s.destinationCity]).filter(Boolean))];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <button 
            onClick={() => navigate('/clients')}
            className="btn btn-ghost btn-sm"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{client.companyName}</h1>
            <p className="text-gray-500">{t('clients.client_details_subtitle', 'Client Details and Relationship Management')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">{t('common.status')}:</span>
            <select
              value={client.status}
              onChange={async (e) => {
                const newStatus = e.target.value;
                try {
                  await clientsService.updateClient(client.id, { status: newStatus });
                  setClient({ ...client, status: newStatus });
                  toast.success(t('clients.status_updated', 'Client status updated successfully'));
                } catch (error) {
                  console.error('Error updating client status:', error);
                  toast.error(t('clients.status_update_failed', 'Failed to update client status'));
                }
              }}
              className="input-field min-w-[120px]"
            >
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
              <option value="pending">{t('common.pending')}</option>
            </select>
          </div>
          <AuditHistoryButton module={AUDIT_MODULES.CUSTOMER} recordId={id} />
          <Link to={`/create-shipment?clientId=${client.id}`} className="btn btn-primary">
            <PlusIcon className="w-4 h-4 me-2" />
            {t('shipments.new_shipment', 'New Shipment')}
          </Link>
          <Link to={`/clients/${client.id}/edit`} className="btn btn-outline">
            <PencilIcon className="w-4 h-4 me-2" />
            {t('clients.edit_client', 'Edit Client')}
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <ChartBarIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">SAR {client.avgShipmentValue?.toLocaleString('en-US') || 0}</div>
            <div className="text-sm text-gray-500">{t('clients.avg_shipment_value', 'Average Shipment Value')}</div>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <CurrencyDollarIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">SAR {client.totalRevenue?.toLocaleString('en-US') || 0}</div>
            <div className="text-sm text-gray-500">{t('clients.total_revenue', 'Total Revenue')}</div>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <TruckIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">{client.totalShipments || 0}</div>
            <div className="text-sm text-gray-500">{t('clients.total_shipments', 'Total Shipments')}</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 rtl:space-x-reverse">
          <button
            onClick={() => setActiveTab('shipments')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'shipments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <TruckIcon className="w-5 h-5 inline me-2" />
            {t('shipments.title', 'Shipments')}
          </button>
          <button
            onClick={() => setActiveTab('client-details')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'client-details'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BuildingOfficeIcon className="w-5 h-5 inline me-2" />
            {t('clients.client_details', 'Client Details')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'client-details' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Client Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{t('clients.client_information', 'Client Information')}</h3>
            </div>
            <div className="card-body space-y-4">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <UserIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium">{client.contactName}</div>
                  <div className="text-sm text-gray-500">{t('clients.contact_name', 'Contact Name')}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium">{client.email}</div>
                  <div className="text-sm text-gray-500">{t('clients.email', t('common.email'))}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <PhoneIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium">{client.phoneNumber}</div>
                  <div className="text-sm text-gray-500">{t('clients.phone_number', 'Phone Number')}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <MapPinIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium">{client.address}</div>
                  <div className="text-sm text-gray-500">{t('clients.address', 'Address')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{t('clients.business_information', 'Business Information')}</h3>
            </div>
            <div className="card-body space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('clients.payment_terms', 'Payment Terms')}</span>
                <span className="font-medium">{client.paymentTerms} {t('common.days', 'days')}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-500">{t('clients.credit_limit', 'Credit Limit')}</span>
                <span className="font-medium">SAR {client.creditLimit?.toLocaleString('en-US')}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-500">{t('clients.last_shipment', 'Last Shipment')}</span>
                <span className="font-medium">{client.lastShipment || t('common.none', 'None')}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-500">{t('clients.account_status', 'Account Status')}</span>
                {getStatusBadge(client.status)}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'shipments' && (
        <div className="space-y-6">
          {/* Prominent Filtered Total */}
          {hasActiveFilters && (
            <div className="card bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900">{t('common.filtered_results', 'Filtered Results')}</h3>
                    <p className="text-sm text-blue-600">{t('common.shipment_count', 'Shipment Count')}: {filteredShipments.length}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-900">
                      SAR {filteredTotal.toLocaleString('en-US')}
                    </div>
                    <div className="text-sm text-blue-600">{t('common.total_value', 'Total Value')}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="card-title">{t('clients.client_shipments', 'Client Shipments')}</h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`btn btn-outline btn-sm ${showFilters ? 'btn-active' : ''}`}
                >
                  <FunnelIcon className="w-4 h-4 me-2" />
                  {showFilters ? t('common.hide_filters', 'Hide Filters') : t('common.show_filters', 'Show Filters')}
                  {hasActiveFilters && (
                    <span className="badge badge-sm badge-primary ms-2">
                      {[paymentFilter !== 'all', dateFromFilter, dateToFilter, cityFilter !== 'all'].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="card-body border-t bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Payment Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.payment_status', 'Payment Status')}</label>
                    <select 
                      value={paymentFilter} 
                      onChange={(e) => setPaymentFilter(e.target.value)}
                      className="form-select w-full"
                    >
                      <option value="all">{t('common.all_statuses', 'All Statuses')}</option>
                      <option value="paid">{t('shipments.paid', t('common.paid'))}</option>
                      <option value="not_paid">{t('shipments.not_paid', 'Not Paid')}</option>
                    </select>
                  </div>

                  {/* Date From Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.from_date', 'From Date')}</label>
                    <input
                      type="date"
                      value={dateFromFilter}
                      onChange={(e) => setDateFromFilter(e.target.value)}
                      className="form-input w-full"
                    />
                  </div>

                  {/* Date To Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.to_date', 'To Date')}</label>
                    <input
                      type="date"
                      value={dateToFilter}
                      onChange={(e) => setDateToFilter(e.target.value)}
                      className="form-input w-full"
                    />
                  </div>

                  {/* City Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('clients.city')}</label>
                    <select 
                      value={cityFilter} 
                      onChange={(e) => setCityFilter(e.target.value)}
                      className="form-select w-full"
                    >
                      <option value="all">{t('common.all_cities', 'All Cities')}</option>
                      {allCities.map(city => (
                        <option key={city} value={city}>
                          {getCityName(city)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setPaymentFilter('all');
                        setDateFromFilter('');
                        setDateToFilter('');
                        setCityFilter('all');
                      }}
                      className="btn btn-ghost btn-sm"
                    >
                      <XMarkIcon className="w-4 h-4 me-2" />
                      {t('common.clear_all_filters', 'Clear All Filters')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Shipments Table */}
          <div className="card">
            {/* Bulk Actions Bar */}
            {selectedShipments.length > 0 && (
              <div className="card-body border-b bg-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 rtl:space-x-reverse">
                    <span className="text-sm font-medium text-blue-900">
                      تم تحديد {selectedShipments.length} شحنة
                    </span>
                    <button
                      onClick={() => setSelectedShipments([])}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      إلغاء التحديد
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <button
                      onClick={() => handleBulkPaymentUpdate(true)}
                      disabled={bulkActionLoading}
                      className="btn btn-success btn-sm"
                    >
                      {bulkActionLoading ? 'جاري التحديث...' : 'تحديد كمدفوع'}
                    </button>
                    <button
                      onClick={() => handleBulkPaymentUpdate(false)}
                      disabled={bulkActionLoading}
                      className="btn btn-danger btn-sm"
                    >
                      {bulkActionLoading ? 'جاري التحديث...' : 'تحديد كغير مدفوع'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="card-body p-0">
              <div className="overflow-x-auto">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={checkbox => {
                            if (checkbox) checkbox.indeterminate = someSelected;
                          }}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="form-checkbox"
                        />
                      </th>
                      <th>{t('common.actions', t('common.actions'))}</th>
                      <th>{t('common.value', 'Value')}</th>
                      <th>{t('common.status', t('common.status'))}</th>
                      <th>{t('shipments.driver', 'Driver')}</th>
                      <th>{t('shipments.vehicle', 'Vehicle')}</th>
                      <th>{t('shipments.route', 'Route')}</th>
                      <th>{t('common.date_time', 'Date & Time')}</th>
                      <th>{t('shipments.shipment_number', 'Shipment Number')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShipments.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8">
                          <div className="text-gray-500">
                            {hasActiveFilters ? t('common.no_matching_shipments', 'No shipments match the selected filters') : t('clients.no_shipments_for_client', 'No shipments for this client')}
                          </div>
                          {!hasActiveFilters && (
                            <Link to={`/create-shipment?clientId=${client.id}`} className="btn btn-primary btn-sm mt-2">
                              {t('shipments.create_first_shipment', 'Create First Shipment')}
                            </Link>
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredShipments.map((shipment) => {
                        const dateTime = formatDateTime(shipment.createdAt);
                        const isPaid = shipment.status === 'delivered' || shipment.paymentStatus === 'paid';
                        const shipmentNumber = getShipmentNumber(shipment);
                        const isSelected = selectedShipments.includes(shipment.id);
                        
                        return (
                          <tr key={shipment.id} className={`${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                            <td>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleShipmentSelect(shipment.id)}
                                className="form-checkbox"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td>
                              <Link 
                                to={`/shipments/${shipment.id}`} 
                                className="btn btn-ghost btn-sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <EyeIcon className="w-4 h-4" />
                              </Link>
                            </td>
                            <td 
                              className="cursor-pointer"
                              onClick={() => handleShowShipmentDetails(shipment)}
                            >
                              <div>
                                <div className="font-medium">SAR {calculateSubtotalBeforeTax(shipment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                <div className={`text-xs ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                                  {isPaid ? `✓ ${t('shipments.paid', t('common.paid'))}` : `✗ ${t('shipments.not_paid', 'Not Paid')}`}
                                </div>
                              </div>
                            </td>
                            <td 
                              className="cursor-pointer"
                              onClick={() => handleShowShipmentDetails(shipment)}
                            >
                              {getStatusBadge(shipment.status)}
                            </td>
                            <td 
                              className="cursor-pointer"
                              onClick={() => handleShowShipmentDetails(shipment)}
                            >
                              <div className="text-sm">
                                <div className="font-medium text-blue-600">
                                  {(() => {
                                    const driver = getDriverInfo(shipment.driverId);
                                    if (driver?.nickname && driver.nickname.trim()) {
                                      return driver.nickname;
                                    } else if (driver?.name) {
                                      return driver.name;
                                    } else {
                                      return shipment.driverName || t('common.not_specified', 'Not Specified');
                                    }
                                  })()}
                                </div>
                              </div>
                            </td>
                            <td 
                              className="cursor-pointer"
                              onClick={() => handleShowShipmentDetails(shipment)}
                            >
                              <div className="text-sm">
                                <div className="font-medium">
                                  {(() => {
                                    // For external drivers, show "خارجي" (External)
                                    if (shipment.isExternalDriver) {
                                      return t('common.external', 'External');
                                    }
                                    
                                    // First check if the shipment has a direct vehiclePlate
                                    if (shipment.vehiclePlate && shipment.vehiclePlate !== 'Unknown Vehicle') {
                                      return shipment.vehiclePlate;
                                    }
                                    
                                    // If not, try to find the vehicle by vehicleId
                                    if (shipment.vehicleId) {
                                      const vehicle = getVehicleInfo(shipment.vehicleId);
                                      if (vehicle) {
                                        return vehicle.plateNumber;
                                      }
                                    }
                                    
                                    return t('common.not_specified', 'Not Specified');
                                  })()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {(() => {
                                    // For external drivers, show the selected truck type
                                    if (shipment.isExternalDriver) {
                                      return getTruckTypeName(shipment.truckType) || '';
                                    }
                                    
                                    let vehicle = null;
                                    
                                    // Find the vehicle by vehicleId first
                                    if (shipment.vehicleId) {
                                      vehicle = getVehicleInfo(shipment.vehicleId);
                                    }
                                    
                                    // If not found, try by plate
                                    if (!vehicle && shipment.vehiclePlate && shipment.vehiclePlate !== 'Unknown Vehicle') {
                                      vehicle = getVehicleInfo(shipment.vehiclePlate);
                                    }
                                    
                                    if (vehicle) {
                                      return getTruckTypeName(vehicle.truckType || vehicle.type) || '';
                                    }
                                    return '';
                                  })()}
                                </div>
                              </div>
                            </td>
                            <td 
                              className="cursor-pointer"
                              onClick={() => handleShowShipmentDetails(shipment)}
                            >
                              {(() => {
                                const route = formatRouteDisplay(shipment);
                                return (
                                  <div className="text-sm">
                                    <div className="font-medium">{route.origin}</div>
                                    <div className="text-xs text-gray-500">↓</div>
                                    <div className="font-medium">{route.destination}</div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td 
                              className="cursor-pointer"
                              onClick={() => handleShowShipmentDetails(shipment)}
                            >
                              <div className="text-sm">
                                <div className="font-medium">{dateTime.date}</div>
                                <div className="text-xs text-gray-500">{dateTime.time}</div>
                              </div>
                            </td>
                            <td 
                              className="cursor-pointer"
                              onClick={() => handleShowShipmentDetails(shipment)}
                            >
                              <span className="text-blue-600 hover:text-blue-800 font-mono text-sm font-medium">
                                {shipmentNumber}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipment Details Modal */}
      {showShipmentModal && selectedShipmentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}
          >
            <div className="card-header flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-xl font-bold">
                {i18n.language === 'ar' ? `تفاصيل الشحنة ${getShipmentNumber(selectedShipmentDetails)}` : `Shipment Details ${getShipmentNumber(selectedShipmentDetails)}`}
              </h2>
              <button
                onClick={() => setShowShipmentModal(false)}
                className="btn btn-ghost btn-sm"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="card-body space-y-6 p-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">معلومات أساسية</h3>
                  </div>
                  <div className="card-body space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">رقم الشحنة:</span>
                      <span className="font-medium font-mono" style={{ direction: 'ltr' }}>{getShipmentNumber(selectedShipmentDetails)}</span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">العميل:</span>
                      <span className="font-medium">{selectedShipmentDetails.clientName}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">الحالة:</span>
                      <div>
                        {getStatusBadge(selectedShipmentDetails.status)}
                      </div>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">حالة الدفع:</span>
                      <span className={`font-medium ${(selectedShipmentDetails.status === 'delivered' || selectedShipmentDetails.paymentStatus === 'paid') ? 'text-green-600' : 'text-red-600'}`}>
                        {(selectedShipmentDetails.status === 'delivered' || selectedShipmentDetails.paymentStatus === 'paid') ? '✓ مدفوع' : '✗ غير مدفوع'}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">التكلفة:</span>
                                                <span className="font-medium text-green-600" style={{ direction: 'ltr' }}>SAR {calculateSubtotalBeforeTax(selectedShipmentDetails).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || t('common.not_specified', 'Not Specified')}</span>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">{t('shipments.route_information', 'Route Information')}</h3>
                  </div>
                  <div className="card-body space-y-3">
                    {(() => {
                      const route = formatRouteDisplay(selectedShipmentDetails);
                      return (
                        <>
                          <div className="flex justify-between items-start">
                            <span className="text-gray-600">{t('common.from', 'From')}:</span>
                            <span className="font-medium">{route.origin}</span>
                          </div>
                          <div className="flex justify-between items-start">
                            <span className="text-gray-600">{t('common.to', 'To')}:</span>
                            <span className="font-medium">{route.destination}</span>
                          </div>
                        </>
                      );
                    })()}
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">{t('shipments.pickup_date', 'Pickup Date')}:</span>
                      <span className="font-medium" style={{ direction: 'ltr' }}>{formatDateTime(selectedShipmentDetails.pickupDate).date}</span>
                    </div>
                    {selectedShipmentDetails.expectedDeliveryDate && (
                      <div className="flex justify-between items-start">
                        <span className="text-gray-600">{t('shipments.expected_delivery_date', 'Expected Delivery Date')}:</span>
                        <span className="font-medium" style={{ direction: 'ltr' }}>{formatDateTime(selectedShipmentDetails.expectedDeliveryDate).date}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Driver and Vehicle Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">{t('shipments.driver_information', 'Driver Information')}</h3>
                  </div>
                  <div className="card-body space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">{t('shipments.driver_name', 'Driver Name')}:</span>
                      <span className="font-medium">
                        {(() => {
                          const driver = getDriverInfo(selectedShipmentDetails.driverId);
                          if (driver?.nickname && driver.nickname.trim()) {
                            return driver.nickname;
                          } else if (driver?.name) {
                            return driver.name;
                          } else {
                            return selectedShipmentDetails.driverName || t('common.not_specified', 'Not Specified');
                          }
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">{t('shipments.driver_id', 'Driver ID')}:</span>
                      <span className="font-medium" style={{ direction: 'ltr' }}>{selectedShipmentDetails.driverId || t('common.not_specified', 'Not Specified')}</span>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">{t('shipments.vehicle_information', 'Vehicle Information')}</h3>
                  </div>
                  <div className="card-body space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">{t('shipments.vehicle_plate', 'Vehicle Plate')}:</span>
                      <span className="font-medium" style={{ direction: 'ltr' }}>
                        {(() => {
                          // For external drivers, show "خارجي" (External)
                          if (selectedShipmentDetails.isExternalDriver) {
                            return t('common.external', 'External');
                          }
                          
                          // First check if the shipment has a direct vehiclePlate
                          if (selectedShipmentDetails.vehiclePlate && selectedShipmentDetails.vehiclePlate !== 'Unknown Vehicle') {
                            return selectedShipmentDetails.vehiclePlate;
                          }
                          
                          // If not, try to find the vehicle by vehicleId
                          if (selectedShipmentDetails.vehicleId) {
                            const vehicle = getVehicleInfo(selectedShipmentDetails.vehicleId);
                            if (vehicle) {
                              return vehicle.plateNumber;
                            }
                          }
                          
                          return t('common.not_specified', 'Not Specified');
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">{t('shipments.vehicle_details', 'Vehicle Details')}:</span>
                      <span className="font-medium">
                        {(() => {
                          // For external drivers, show the selected truck type
                          if (selectedShipmentDetails.isExternalDriver) {
                            return getTruckTypeName(selectedShipmentDetails.truckType) || t('shipments.vehicle_information', 'Vehicle Information');
                          }
                          
                          let vehicle = null;
                          
                          // Find the vehicle by vehicleId first
                          if (selectedShipmentDetails.vehicleId) {
                            vehicle = getVehicleInfo(selectedShipmentDetails.vehicleId);
                          }
                          
                          // If not found, try by plate
                          if (!vehicle && selectedShipmentDetails.vehiclePlate && selectedShipmentDetails.vehiclePlate !== 'Unknown Vehicle') {
                            vehicle = getVehicleInfo(selectedShipmentDetails.vehiclePlate);
                          }
                          
                          if (vehicle) {
                            return getTruckTypeName(vehicle.truckType || vehicle.type) || t('shipments.vehicle_information', 'Vehicle Information');
                          }
                          return t('common.not_specified', 'Not Specified');
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cargo Information */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">معلومات البضاعة</h3>
                </div>
                <div className="card-body space-y-3">
                  {selectedShipmentDetails.cargoDescription && (
                    <div>
                      <span className="text-gray-600 block mb-1">وصف البضاعة:</span>
                      <span className="font-medium">{selectedShipmentDetails.cargoDescription}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {selectedShipmentDetails.cargoWeight && (
                      <div className="flex justify-between items-start border rounded p-3">
                        <span className="text-gray-600">الوزن:</span>
                        <span className="font-medium" style={{ direction: 'ltr' }}>{selectedShipmentDetails.cargoWeight} كجم</span>
                      </div>
                    )}
                    {selectedShipmentDetails.totalValue && (
                      <div className="flex justify-between items-start border rounded p-3">
                        <span className="text-gray-600">القيمة المصرح بها:</span>
                        <span className="font-medium" style={{ direction: 'ltr' }}>SAR {selectedShipmentDetails.totalValue?.toLocaleString('en-US')}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-start border rounded p-3">
                      <span className="text-gray-600">نوع الخدمة:</span>
                      <span className="font-medium">{selectedShipmentDetails.serviceType === 'dry' ? 'جاف' : selectedShipmentDetails.serviceType === 'chilled' ? 'مبرد' : 'مجمد'}</span>
                    </div>
                  </div>
                  {selectedShipmentDetails.notes && (
                    <div>
                      <span className="text-gray-600 block mb-1">ملاحظات:</span>
                      <span className="font-medium">{selectedShipmentDetails.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">الجدول الزمني</h3>
                </div>
                <div className="card-body">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600">تاريخ الإنشاء:</span>
                      <span className="font-medium" style={{ direction: 'ltr' }}>
                        {formatDateTime(selectedShipmentDetails.createdAt).date} في {formatDateTime(selectedShipmentDetails.createdAt).time}
                      </span>
                    </div>
                    {selectedShipmentDetails.updatedAt && (
                      <div className="flex justify-between items-start">
                        <span className="text-gray-600">آخر تحديث:</span>
                        <span className="font-medium" style={{ direction: 'ltr' }}>
                          {formatDateTime(selectedShipmentDetails.updatedAt).date} في {formatDateTime(selectedShipmentDetails.updatedAt).time}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card-footer flex items-center justify-between border-t px-6 py-4">
              <Link 
                to={`/shipments/${selectedShipmentDetails.id}`}
                className="btn btn-primary"
                onClick={() => setShowShipmentModal(false)}
              >
                عرض صفحة الشحنة الكاملة
              </Link>
              <button
                onClick={() => setShowShipmentModal(false)}
                className="btn btn-secondary"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail; 